import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ParentSettlements.css';
import { API_BASE_URL } from '../services/api';

const ParentSettlements = () => {
  const navigate = useNavigate();
  const [settlementData, setSettlementData] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState('');
  const [settlementHistory, setSettlementHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isAlreadySettled, setIsAlreadySettled] = useState(false);
  const [canSettleToday, setCanSettleToday] = useState(false);
  const [hasPendingApprovals, setHasPendingApprovals] = useState(false);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/parent/login');
      return;
    }

    // 현재 주로 초기화 (월요일 기준)
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0=일요일, 1=월요일, ..., 6=토요일
    const startDate = new Date(today);

    // 월요일을 주의 시작으로 계산
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // 일요일이면 6일 전, 나머지는 dayOfWeek-1일 전
    startDate.setDate(today.getDate() - daysFromMonday);

    const weekString = startDate.toISOString().split('T')[0];
    setSelectedWeek(weekString);

    // 정산 이력을 먼저 로드한 후 미리보기 로드
    loadSettlementHistory().then(() => {
      if (weekString) {
        loadSettlementPreview();
      }
    });
  }, [navigate]);

  // 정산 가능 요일 체크 함수
  const checkCanSettleToday = () => {
    const today = new Date();
    const currentDayOfWeek = today.getDay(); // 0=일요일, 6=토요일
    const selectedDate = new Date(selectedWeek);

    // 현재 주의 월요일 계산
    const currentDayOffset = currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1;
    const currentWeekStart = new Date(today);
    currentWeekStart.setDate(today.getDate() - currentDayOffset);
    currentWeekStart.setHours(0, 0, 0, 0);

    // 선택된 주의 월요일
    const selectedWeekStart = new Date(selectedDate);
    selectedWeekStart.setHours(0, 0, 0, 0);

    // 과거 주는 언제나 정산 가능
    if (selectedWeekStart < currentWeekStart) {
      setCanSettleToday(true);
      return;
    }

    // 현재 주인 경우 토요일(6) 또는 일요일(0)만 가능
    if (selectedWeekStart.getTime() === currentWeekStart.getTime()) {
      const isWeekend = currentDayOfWeek === 0 || currentDayOfWeek === 6;
      setCanSettleToday(isWeekend);
      return;
    }

    // 미래 주는 불가
    setCanSettleToday(false);
  };

  useEffect(() => {
    if (selectedWeek) {
      // 주차가 바뀔 때마다 정산 상태 초기화 및 확인
      const isSettledInStorage = localStorage.getItem(`settled_${selectedWeek}`) === 'true';
      if (isSettledInStorage) {
        setIsAlreadySettled(true);
      } else {
        setIsAlreadySettled(false);
        loadSettlementPreview();
      }

      // 정산 가능 요일 체크 (토요일, 일요일만 가능)
      checkCanSettleToday();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWeek]);

  // 데이터가 없고 로딩도 아닐 때 자동 재시도
  useEffect(() => {
    if (!settlementData && !loading && selectedWeek) {
      const timer = setTimeout(() => {
        console.log('자동 재시도: 정산 데이터 로드');
        loadSettlementPreview();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [settlementData, loading, selectedWeek]);

  const loadSettlementPreview = async () => {
    if (!selectedWeek) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/settlements/preview?week=${selectedWeek}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Settlement preview data:', data);
        setSettlementData(data);

        // 승인 대기 건 확인
        setHasPendingApprovals(data.hasPendingApprovals || false);
        setPendingApprovalsCount(data.pendingApprovalsCount || 0);

        // 이미 정산된 주인지 확인 - API 응답에서 직접 확인
        const isSettledFromAPI = data.isAlreadySettled;
        const isSettledFromHistory = settlementHistory.some(history =>
          history.week === selectedWeek
        );
        const isSettled = isSettledFromAPI || isSettledFromHistory;

        console.log('정산 상태 확인:', {
          selectedWeek,
          isSettledFromAPI,
          isSettledFromHistory,
          isSettled,
          settlementHistoryLength: settlementHistory.length,
          hasPendingApprovals: data.hasPendingApprovals
        });

        // 로컬스토리지에서도 확인
        const isSettledInStorage = localStorage.getItem(`settled_${selectedWeek}`) === 'true';
        const finalSettled = isSettled || isSettledInStorage;

        setIsAlreadySettled(finalSettled);
      } else {
        console.error('Settlement preview failed:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error('정산 미리보기 로딩 실패');
      }
    } catch (error) {
      console.error('정산 미리보기 오류:', error);
      setMessage('정산 데이터를 불러올 수 없습니다.');
      // 오류 발생 시 정산 상태 초기화
      setIsAlreadySettled(false);
    } finally {
      setLoading(false);
    }
  };

  const loadSettlementHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/settlements/history`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSettlementHistory(data.settlements || []);
      }
    } catch (error) {
      console.error('정산 이력 오류:', error);
    }
  };

  const handleSettlementConfirm = async () => {
    if (!settlementData || settlementData.children.length === 0) {
      setMessage('정산할 데이터가 없습니다.');
      return;
    }

    // 미래 날짜 정산 방지 - 토요일/일요일에는 정산 가능
    const today = new Date();
    const selectedDate = new Date(selectedWeek);
    const weekEnd = new Date(selectedDate);
    weekEnd.setDate(selectedDate.getDate() + 6); // 주 마지막 날 (일요일)

    // 현재 요일 확인 (0=일요일, 6=토요일)
    const currentDayOfWeek = today.getDay();
    const isWeekend = currentDayOfWeek === 0 || currentDayOfWeek === 6; // 토요일 또는 일요일

    // 진짜 미래 주간인지 확인 (현재 주가 아닌 미래 주)
    const currentWeekStart = new Date(today);
    const currentDayOffset = currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1;
    currentWeekStart.setDate(today.getDate() - currentDayOffset);
    currentWeekStart.setHours(0, 0, 0, 0);

    const selectedWeekStart = new Date(selectedDate);
    selectedWeekStart.setHours(0, 0, 0, 0);

    // 선택된 주가 현재 주보다 미래인 경우에만 차단
    if (selectedWeekStart > currentWeekStart) {
      setMessage('❌ 미래 주간은 정산할 수 없습니다.');
      return;
    }

    // 현재 주인 경우 토요일/일요일이 아니면 정산 불가
    if (selectedWeekStart.getTime() === currentWeekStart.getTime() && !isWeekend) {
      setMessage('⏰ 토요일, 일요일에만 정산 가능합니다.');
      return;
    }

    if (isAlreadySettled) {
      setMessage('❌ 이미 정산이 완료된 주입니다.');
      return;
    }

    // 승인 대기 건 확인
    if (hasPendingApprovals) {
      setMessage(`❌ 승인 대기 중인 미션 ${pendingApprovalsCount}건을 먼저 처리해주세요.`);
      return;
    }

    // 추가 확인: 서버에 요청하기 전에 정산 이력 재확인
    const alreadySettledInHistory = settlementHistory.some(history =>
      history.week === selectedWeek
    );

    if (alreadySettledInHistory) {
      console.log('정산 이력에서 발견됨:', selectedWeek, settlementHistory);
      setIsAlreadySettled(true);
      setMessage('❌ 이미 정산이 완료된 주입니다.');
      return;
    }

    console.log('정산 실행 전 상태:', {
      selectedWeek,
      isAlreadySettled,
      settlementHistoryCount: settlementHistory.length,
      hasChildrenData: settlementData.children.length > 0
    });

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const childrenData = settlementData.children.map(child => ({
        childId: child.child.id,
        totalMissions: child.stats.totalMissions,
        completedMissions: child.stats.completedMissions,
        completionRate: child.stats.completionRate,
        baseAllowance: child.calculation.baseAllowance,
        finalAmount: child.calculation.finalAmount,
        bonusAmount: child.calculation.bonusAmount,
        penaltyAmount: child.calculation.penaltyAmount
      }));

      const response = await fetch(`${API_BASE_URL}/settlements/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          week: selectedWeek,
          children: childrenData
        })
      });

      if (response.ok) {
        const result = await response.json();
        setMessage(`✅ ${result.message}`);

        // 즉시 정산 완료 상태로 설정
        setIsAlreadySettled(true);

        await loadSettlementHistory(); // 이력 새로고침

        // 자동으로 다음 주로 이동
        const nextWeek = new Date(selectedWeek);
        nextWeek.setDate(nextWeek.getDate() + 7);
        const nextWeekString = nextWeek.toISOString().split('T')[0];

        // 2초 후 다음 주로 자동 이동
        setTimeout(() => {
          setSelectedWeek(nextWeekString);
          setMessage(`📅 다음 주(${nextWeekString})로 이동했습니다.`);
          setTimeout(() => setMessage(''), 3000);
        }, 2000);
      } else {
        const error = await response.json();
        throw new Error(error.error || '정산 확정 실패');
      }
    } catch (error) {
      if (error.message.includes('이미 정산된 주')) {
        setIsAlreadySettled(true);
        localStorage.setItem(`settled_${selectedWeek}`, 'true');
      }
      console.error('정산 확정 오류:', error);
      setMessage(`❌ ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
      weekday: 'short'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ko-KR').format(amount);
  };

  if (loading && !settlementData) {
    return <div className="loading">로딩 중...</div>;
  }

  return (
    <div className="parent-settlements">
      <div className="settlements-header">
        <button className="back-btn" onClick={() => navigate('/parent/dashboard')}>
          ← 뒤로가기
        </button>
        <h1>주간 정산</h1>
      </div>


      {/* 주간 선택 */}
      <div className="week-selector">
        <label htmlFor="week-select">정산 주간 선택:</label>
        <input
          type="date"
          id="week-select"
          value={selectedWeek}
          onChange={(e) => setSelectedWeek(e.target.value)}
        />
        <span className="week-info">
          {selectedWeek && (() => {
            const selectedDate = new Date(selectedWeek);
            const dayOfWeek = selectedDate.getDay();

            // 월요일을 주의 시작으로 계산
            const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // 일요일이면 6일 전, 나머지는 dayOfWeek-1일 전
            const weekStart = new Date(selectedDate);
            weekStart.setDate(selectedDate.getDate() - mondayOffset);

            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 6);

            return `${formatDate(weekStart)} ~ ${formatDate(weekEnd)}`;
          })()}
        </span>
      </div>

      {/* 정산 미리보기 */}
      <div className="settlement-preview">
        {!settlementData && loading && (
          <div className="loading">정산 데이터를 불러오는 중...</div>
        )}
        {!settlementData && !loading && (
          <div className="no-data">
            <p>정산 데이터를 준비 중입니다...</p>
          </div>
        )}
        {settlementData && (
          <>
            <h2>🧮 정산 미리보기</h2>

          <div className="settlement-summary">
            <div className="summary-item">
              <span>총 지급 예정액:</span>
              <strong>{formatCurrency(settlementData.summary?.totalFamilyAmount || 0)}원</strong>
            </div>
            <div className="summary-item">
              <span>평균 완료율:</span>
              <strong>{settlementData.summary?.averageCompletionRate || 0}%</strong>
            </div>
          </div>

          <div className="children-settlements">
            {settlementData.children.map((childData) => (
              <div key={childData.child.id} className="child-settlement-card">
                <div className="child-header">
                  <h3>{childData.child.name}</h3>
                  <div className="completion-badge">
                    {childData.stats.completionRate}% 완료
                  </div>
                </div>

                <div className="settlement-details">
                  <div className="detail-row">
                    <span>총 미션:</span>
                    <span>{childData.stats.totalMissions}개</span>
                  </div>
                  <div className="detail-row">
                    <span>완료 미션:</span>
                    <span>{childData.stats.completedMissions}개</span>
                  </div>
                  <div className="detail-row">
                    <span>가중 완료율:</span>
                    <span>{childData.stats.weightedCompletionRate}%</span>
                  </div>
                  <div className="detail-row">
                    <span>기본 용돈:</span>
                    <span>{formatCurrency(childData.calculation.baseAllowance)}원</span>
                  </div>
                  {childData.calculation.bonusAmount > 0 && (
                    <div className="detail-row bonus">
                      <span>보너스:</span>
                      <span>+{formatCurrency(childData.calculation.bonusAmount)}원</span>
                    </div>
                  )}
                  {childData.calculation.penaltyAmount > 0 && (
                    <div className="detail-row penalty">
                      <span>벌점:</span>
                      <span>-{formatCurrency(childData.calculation.penaltyAmount)}원</span>
                    </div>
                  )}
                  <div className="detail-row final">
                    <span>최종 지급액:</span>
                    <strong>{formatCurrency(childData.calculation.finalAmount)}원</strong>
                  </div>
                </div>

                {!childData.stats.allRequiredCompleted && (
                  <div className="required-warning">
                    ⚠️ 필수 미션 미완료로 인해 제한이 적용될 수 있습니다.
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* 정산 안내 메시지 */}
          <div className="settlement-guidance">
            <div className="guidance-box">
              <h4>⏰ 정산 안내</h4>
              <p>
                <strong>정산 가능 시간:</strong> 토요일, 일요일<br/>
                <strong>자동 정산:</strong> 월요일 00:00에 미정산 가족 자동 처리
              </p>
            </div>
          </div>

          {/* 승인 대기 안내 */}
          {hasPendingApprovals && !isAlreadySettled && (
            <div className="settlement-notice error">
              ⚠️ 승인 대기 중인 미션이 <strong>{pendingApprovalsCount}건</strong> 있습니다. 먼저 승인 처리를 완료해주세요.
            </div>
          )}

          {/* 요일 제한 안내 */}
          {!canSettleToday && !isAlreadySettled && !hasPendingApprovals && (
            <div className="settlement-notice warning">
              ⏰ 현재 주간은 <strong>토요일</strong> 또는 <strong>일요일</strong>에만 정산할 수 있습니다.
            </div>
          )}

          <div className="settlement-actions">
            <button
              className="btn-confirm"
              onClick={handleSettlementConfirm}
              disabled={loading || !settlementData || settlementData.children.length === 0 || isAlreadySettled || !canSettleToday || hasPendingApprovals}
            >
              {loading ? '정산 중...' :
               isAlreadySettled ? '✅ 정산 완료됨' :
               hasPendingApprovals ? `🔒 승인 대기 ${pendingApprovalsCount}건 처리 필요` :
               !canSettleToday ? '🔒 정산 불가 (토/일요일만 가능)' :
               '정산 확정하기'}
            </button>

            {/* 메시지를 버튼 바로 아래에 표시 */}
            {message && (
              <div className={`settlement-message ${message.includes('❌') ? 'error' : 'success'}`}>
                {message}
              </div>
            )}

            {isAlreadySettled && (
              <div className="already-settled-notice">
                ℹ️ 이 주는 이미 정산이 완료되었습니다. 정산 이력에서 확인할 수 있습니다.
              </div>
            )}
          </div>
          </>
        )}
      </div>

      {/* 정산 이력 */}
      <div className="settlement-history">
        <h2>📋 정산 이력</h2>
        {settlementHistory.length === 0 ? (
          <div className="no-history">아직 정산 이력이 없습니다.</div>
        ) : (
          <div className="history-list">
            {settlementHistory.map((settlement, index) => (
              <div key={settlement.week} className="history-item">
                <div className="history-header">
                  <span className="week-period">
                    {formatDate(settlement.week)} ~ {formatDate(settlement.weekEndDate)}
                  </span>
                  <span className="total-amount">
                    {formatCurrency(settlement.totalAmount)}원
                  </span>
                </div>
                <div className="children-list">
                  {settlement.children.map((child) => (
                    <div key={child.id} className="child-summary">
                      <span>{child.name}</span>
                      <span>{child.completionRate}% ({formatCurrency(child.finalAmount)}원)</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentSettlements;